import { Component, ChangeDetectionStrategy, ElementRef, ViewChild, ViewEncapsulation, signal, inject } from '@angular/core';
import { IrisFilesService, IrisFilePreviewType } from '@iris/common/modules/dms/services/files.service';
import { FileUploader } from 'ng2-file-upload';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CropperSettings, ImageCropperComponent, ImageCropperModule } from 'ngx-img-cropper';
import { dataURItoFile } from '@iris/common/utils/file.utils';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisSubjectI } from '@iris/common/models/IrisSubject';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisFileUploaderComponent } from '../../file-uploader/file-uploader.component';
import { NgClass } from '@angular/common';
import { IrisFileSizePipe } from '@iris/common/modules/pipes-common/pipes';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';
import {
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';
import { StrakitCellComponent } from '@ngx-strakit/cdk/layout';

@Component({
  selector: 'files-modal-extended',
  imports: [
    NgClass,
    TranslatePipe,
    ImageCropperModule,
    IrisFileUploaderComponent,
    IrisFileSizePipe,
    SkModalContentComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
    StrakitCellComponent,
  ],
  templateUrl: './files-modal-extended.component.html',
  styleUrls: ['./files-modal-extended.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisFilesModalExtendedComponent extends SkModalBase<IrisFilesModalExtendedComponent, IrisDMSFileI[]> {
  private readonly fileService = inject(IrisFilesService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogData = inject<{
    accept: string;
    subjects: IrisSubjectI[];
    module: IrisModules;
    module_files: IrisDMSFileI[];
    folderId?: string;
    isUploadOnly?: boolean;
  }>(MAT_DIALOG_DATA);

  readonly accept = this.dialogData.accept;
  readonly subjects = this.dialogData.subjects;
  readonly module = this.dialogData.module;
  readonly folderId = this.dialogData.folderId ?? null;
  readonly isUploadOnly = this.dialogData.isUploadOnly ?? false;
  readonly cancelBtnText = this.isUploadOnly ? 'label.Close' : 'label.Cancel';

  /** Files successfully uploaded within this modal session. */
  private readonly uploadedFiles: IrisDMSFileI[] = [];

  readonly moduleFiles = signal(this.dialogData.module_files ?? []);
  @ViewChild('cropper') cropper: ImageCropperComponent;

  readonly cropperSettings = new CropperSettings({
    croppedWidth: 256,
    croppedHeight: 256,
    noFileInput: true,
    dynamicSizing: true,
  });
  data: { image?: string } = {};
  readonly selected_file = signal<IrisDMSFileI>(null);
  cropFieldActive = false;
  closeAfterUpload = false;

  @ViewChild('scrollBody') private readonly scrollContainer: ElementRef<HTMLElement>;

  get parentElement(): HTMLElement {
    return this.scrollContainer?.nativeElement?.parentElement;
  }

  scrollBottom(): void {
    this.parentElement.scrollTop = this.parentElement?.scrollHeight;
  }

  isImage(file: IrisDMSFileI): boolean {
    return this.fileService.getPreviewType(file) === IrisFilePreviewType.Image;
  }

  showCrop(): void {
    this.cropFieldActive = !this.cropFieldActive;
    if (this.cropFieldActive) {
      setTimeout(() => this.scrollBottom());
      if (this.selected_file()) {
        const image = new Image();
        image.crossOrigin = 'Anonymous';
        image.addEventListener('load', () => {
          this.cropper.setImage(image);
        });
        image.src = this.fileService.getFilePreviewUrl(this.selected_file()?.id);
        this.scrollBottom();
      }
    }
  }

  selectFile(file: IrisDMSFileI): void {
    if (this.selected_file()?.id === file.id) {
      this.selected_file.set(null);
      this.cropFieldActive = false;
      return;
    }
    this.selected_file.set(file);
  }

  isPic(file: IrisDMSFileI): number {
    return ~file.mimeType.indexOf('image');
  }

  getFileContent(file_id: string): string {
    return this.fileService.getFilePreviewUrl(file_id);
  }

  getFileIcon(mime_type: string): string {
    return this.fileService.getIcon(mime_type);
  }

  uploadCroppedPicture(uploader: FileUploader): void {
    const imageFormat = this.selected_file()?.mimeType || 'image/png';
    const croppedFileName = `CROPPED_${this.selected_file()?.name || ''}`;
    uploader.addToQueue([dataURItoFile(this.data.image, croppedFileName, imageFormat)]);
    uploader.uploadAll();
  }

  selectFileOrPic(uploader: FileUploader): void {
    if (this.selected_file() && !this.cropFieldActive) {
      this.closeWithResult([this.selected_file()]);
    }

    if (this.cropFieldActive) {
      this.closeAfterUpload = true;
      this.uploadCroppedPicture(uploader);
    }
  }

  /**
   * Handles both the header cross and the footer cancel/close button.
   * In upload-only mode the files uploaded during this session are returned to the caller,
   * so they can be processed (e.g. attached) once the modal is dismissed.
   */
  onCloseModal(): void {
    if (this.isUploadOnly && this.uploadedFiles.length) {
      this.closeWithResult([...this.uploadedFiles]);
      return;
    }
    this.closeWithoutResult();
  }

  onFileSuccessUpload(response: IrisDMSFileI[]): void {
    if (!response?.length) { return; }

    this.toastService.success({ message: this.translateService.instant('text.UploadSuccess') });
    this.uploadedFiles.push(...response);

    if (this.isUploadOnly) { return; }

    response.forEach(file => {
      const moduleFiles = this.moduleFiles();
      const findIndex = moduleFiles.findIndex(({ id }) => id === file.id);
      if (findIndex < 0) {
        moduleFiles.push(file);
      } else {
        moduleFiles[findIndex] = file;
      }
      this.moduleFiles.set(moduleFiles);
    });

    if (this.closeAfterUpload) {
      this.closeWithResult(response);
    }
  }
}
